import { useEffect, useState } from "react";

/** Duration of the glint sweep across the badge (ms), as in the temp worker app. */
export const GLINT_DURATION = 600;

/** Glint window inside a cycle of `cycle` ms, starting at `start` (0..1). */
export const glintWindow = (start, cycle) => [start, start + GLINT_DURATION / cycle];

/** Clamped to [0, 1] and eased at both ends, so a stroke starts and lands without a jolt. */
export const smooth = (value) => {
    const t = Math.min(Math.max(value, 0), 1);
    return t * t * (3 - 2 * t);
};

/** Follows the OS "reduce motion" setting: glyphs then render drawn and still, without glint. */
export const usePrefersReducedMotion = () => {
    const query = "(prefers-reduced-motion: reduce)";
    const [reduced, setReduced] = useState(() => window.matchMedia?.(query).matches ?? false);

    useEffect(() => {
        const media = window.matchMedia?.(query);
        if (!media) return undefined;
        const onChange = (event) => setReduced(event.matches);
        media.addEventListener("change", onChange);
        return () => media.removeEventListener("change", onChange);
    }, []);

    return reduced;
};

/**
 * Runs `render(elapsedMs)` on every animation frame until unmount. `render` writes straight to the
 * SVG nodes: no React render per frame.
 */
export const useAnimationFrame = (render, enabled) => {
    useEffect(() => {
        if (!enabled) return undefined;
        let frame;
        const start = performance.now();
        const tick = (now) => {
            if (render(now - start) === false) return;
            frame = requestAnimationFrame(tick);
        };
        frame = requestAnimationFrame(tick);
        return () => cancelAnimationFrame(frame);
    }, [render, enabled]);
};
